/* R22: one desktop-only clock drives the existing synced Splide pair. */
(() => {
  if (window.__lwProductGallery) return;
  window.__lwProductGallery = true;
  const mounted = new Map();
  const pending = new Map();
  function mountThumbnails(host, slider, desktop) {
    const original = host.querySelector('.switcher-thumb-slider');
    const slides = [...host.querySelectorAll('.main-thumb-slider .splide__slide')];
    const thumbs = [...(original?.querySelectorAll('.splide__slide:not(.splide__slide--clone)') || [])];
    if (thumbs.length < 2 || thumbs.length !== slides.length) return () => {};
    const grid = document.createElement('div');
    grid.className = 'lw-gallery-thumbnails';
    const abort = new AbortController();
    const buttons = thumbs.map((thumb, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'lw-gallery-thumbnail';
      button.setAttribute('aria-label', thumb.getAttribute('aria-label') || slides[index].querySelector('a')?.getAttribute('aria-label') || thumb.querySelector('img')?.alt || String(index + 1));
      button.setAttribute('aria-controls', slider.root.id);
      const image = thumb.querySelector('img')?.cloneNode(true);
      if (image) { image.alt = ''; image.sizes = '130px'; button.append(image); }
      button.addEventListener('click', () => slider.go(index), {signal:abort.signal});
      grid.append(button);
      return button;
    });
    original.after(grid);
    const previousHidden = original.getAttribute('aria-hidden');
    const previousInert = original.inert;
    const sync = () => {
      buttons.forEach((button, index) => button.setAttribute('aria-pressed', String(index === slider.index)));
      if (!desktop.matches) return;
      const selected = buttons[slider.index];
      if (!selected) return;
      const left = selected.offsetLeft, right = left + selected.offsetWidth;
      if (left < grid.scrollLeft) grid.scrollLeft = left;
      else if (right > grid.scrollLeft + grid.clientWidth) grid.scrollLeft = right - grid.clientWidth;
    };
    const layout = () => {
      grid.hidden = !desktop.matches;
      original.classList.toggle('lw-gallery-original-thumbnails', desktop.matches);
      original.inert = desktop.matches || previousInert;
      if (desktop.matches) original.setAttribute('aria-hidden', 'true');
      else if (previousHidden === null) original.removeAttribute('aria-hidden');
      else original.setAttribute('aria-hidden', previousHidden);
      requestAnimationFrame(sync);
    };
    desktop.addEventListener('change', layout, {signal:abort.signal});
    slider.on('moved', sync);
    grid.addEventListener('keydown', event => {
      const index = buttons.indexOf(event.target);
      if (index < 0) return;
      const next = event.key === 'ArrowRight' ? index + 1 : event.key === 'ArrowLeft' ? index - 1 : event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : null;
      if (next === null) return;
      event.preventDefault();
      buttons[Math.max(0, Math.min(buttons.length - 1, next))].focus();
    }, {signal:abort.signal});
    const resize = new ResizeObserver(sync);
    resize.observe(grid);
    layout();
    return () => {
      abort.abort(); resize.disconnect(); slider.off('moved', sync); grid.remove();
      original.classList.remove('lw-gallery-original-thumbnails'); original.inert = previousInert;
      if (previousHidden === null) original.removeAttribute('aria-hidden'); else original.setAttribute('aria-hidden', previousHidden);
    };
  }
  function mountMobileSwipe(host, slider, desktop, slides) {
    if (slides.length < 2) return () => {};
    const original = {
      drag: slider.options.drag,
      perMove: slider.options.perMove,
      flickMaxPages: slider.options.flickMaxPages,
      dragMinThreshold: slider.options.dragMinThreshold,
      noDrag: slider.options.noDrag
    };
    const controls = 'video, video *, iframe, model-viewer, model-viewer *, button, button *, input, select, textarea';
    const update = () => {
      slider.options = desktop.matches ? original : {
        drag: true,
        perMove: 1,
        flickMaxPages: 1,
        dragMinThreshold: { mouse: 4, touch: 10 },
        noDrag: [original.noDrag, controls].filter(Boolean).join(', ')
      };
    };
    desktop.addEventListener('change', update);
    update();
    return () => {
      desktop.removeEventListener('change', update);
      slider.options = original;
    };
  }
  function mount(host, attempt = 0) {
    if (mounted.has(host) || !host.isConnected) return;
    const slider = host.slider;
    if (!slider) {
      if (attempt < 40) pending.set(host, setTimeout(() => { pending.delete(host); mount(host, attempt + 1); }, 100));
      return;
    }
    const slides = [...host.querySelectorAll('.main-thumb-slider .splide__slide')];
    const images = slides.flatMap((slide, index) => slide.dataset.mediaType === 'image' ? [index] : []);
    const section = host.closest('.main-product');
    const desktop = matchMedia('(min-width: 992px)');
    const disposeThumbnails = mountThumbnails(host, slider, desktop);
    const disposeSwipe = mountMobileSwipe(host, slider, desktop, slides);
    const disposeGallery = () => { disposeSwipe(); disposeThumbnails(); };
    if (images.length < 2 || !host.hasAttribute('data-lw-gallery-autoplay')) {
      mounted.set(host, disposeGallery);
      return;
    }
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const interval = Math.min(12000, Math.max(3000, Number(host.dataset.lwGalleryInterval) || 5000));
    const speed = Math.min(1000, Math.max(100, Number(host.dataset.lwGallerySpeed) || 400));
    const originalSpeed = slider.options.speed;
    const abort = new AbortController();
    let requested = true, intersecting = false, hovering = false, timer = 0;
    const clear = () => { clearTimeout(timer); timer = 0; };
    const allowed = () => requested && desktop.matches && !reduced.matches &&
      !document.hidden && intersecting && !hovering && !host.contains(document.activeElement) &&
      !(window.Shopify && window.Shopify.designMode) && images.includes(slider.index);
    const schedule = () => {
      clear();
      if (allowed()) timer = setTimeout(() => {
        timer = 0;
        if (allowed()) {
          const next = images[(images.indexOf(slider.index) + 1) % images.length];
          slider.go(next);
        }
        schedule();
      }, interval);
    };
    const update = () => {
      slider.options = { speed: reduced.matches ? 0 : desktop.matches ? speed : originalSpeed };
      schedule();
    };
    const stop = () => { requested = false; update(); };
    const on = (node, event, fn) => node?.addEventListener(event, fn, { signal: abort.signal });
    on(host, 'pointerenter', () => { hovering = true; schedule(); });
    on(host, 'pointerleave', () => { hovering = false; schedule(); });
    on(host, 'focusin', schedule);
    on(host, 'focusout', () => queueMicrotask(schedule));
    on(host, 'pointerdown', stop);
    on(host, 'keydown', event => { if (['Enter', ' ', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) stop(); });
    on(section, 'change', stop); // Variant selection always wins over automatic images.
    on(document, 'visibilitychange', schedule);
    on(desktop, 'change', update);
    on(reduced, 'change', update);
    const observer = new IntersectionObserver(entries => {
      intersecting = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.15;
      schedule();
    }, { threshold: [0, 0.15] });
    observer.observe(slider.root);
    mounted.set(host, () => { clear(); observer.disconnect(); abort.abort(); disposeGallery(); });
    update();
  }
  const scan = () => {
    if (!document.body.classList.contains('lw-theme')) return;
    document.querySelectorAll('.main-product [data-lw-gallery]').forEach(host => {
      if (!pending.has(host)) mount(host);
    });
  };
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', event => {
    mounted.forEach((cleanup, host) => { if (event.target.contains(host)) { cleanup(); mounted.delete(host); } });
    pending.forEach((timer, host) => { if (event.target.contains(host)) { clearTimeout(timer); pending.delete(host); } });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
  window.addEventListener('load', scan, { once: true });
})();
